import React, { useState } from 'react';
import { useBiometricsList } from './hooks/useBiometricsList';
import SharedFilters from '../../../components/SharedFilters';
import AppPagination from '../../../components/AppPagination';
import BiometricsTable from './components/BiometricsTable';
import BiometricsUploadModal from './components/BiometricsUploadModal';
import SwipeDetailModal from './components/SwipeDetailModal';

export default function BiometricsListScreen() {
  const { state, actions } = useBiometricsList();
  const [detailRecord, setDetailRecord] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const handleViewDetail = (item) => {
    setDetailRecord(item);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailModalOpen(false);
    setDetailRecord(null);
  };

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Banner */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Biometric Records
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">
            Swipe card + approved manual punch entries per employee per day
          </p>
        </div>
      </section>

      {/* Shared Filters */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        showStatus={false}
        companyFilter={state.companyFilter}
        branchFilter={state.branchFilter}
        departmentFilter={state.departmentFilter}
        genderFilter={state.genderFilter}
        empGroupFilter={state.empGroupFilter}
        employeeFilter={state.employeeFilter}
        datePresetFilter={state.datePresetFilter}
        fromDate={state.fromDate}
        toDate={state.toDate}

        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        setCompanyFilter={actions.setCompanyFilter}
        setBranchFilter={actions.setBranchFilter}
        setDepartmentFilter={actions.setDepartmentFilter}
        setGenderFilter={actions.setGenderFilter}
        setEmpGroupFilter={actions.setEmpGroupFilter}
        setEmployeeFilter={actions.setEmployeeFilter}
        setDatePresetFilter={actions.setDatePresetFilter}
        setFromDate={actions.setFromDate}
        setToDate={actions.setToDate}

        onSearch={actions.handleSearch}
        onReset={actions.resetFilters}

        searchFieldOptions={state.searchFieldOptions}
        genderOptions={state.genderOptions}
        datePresetOptions={state.datePresetOptions}

        visibleAccordionFilters={[
          'company',
          'branch',
          'department',
          'gender',
          'empGroup',
          'employee',
          'datePreset',
          'fromDate',
          'toDate',
        ]}

        tools={state.tools}
        onToolSelect={actions.handleToolSelect}
      />

      {/* Biometrics Table */}
      <section className="space-y-4">
        <BiometricsTable
          records={state.records}
          isLoading={state.isLoading}
          onViewDetail={handleViewDetail}
          onDelete={actions.handleDelete}
          onResetFilters={actions.resetFilters}
        />

        {/* Pagination Footer */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest rounded-2xl shadow-xs">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing{' '}
            {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
            {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
          </p>
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage((p) => Math.max(p - 1, 1))}
            onNext={() => actions.setPage((p) => Math.min(p + 1, state.totalPages))}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>

      {/* Biometric Upload Modal */}
      <BiometricsUploadModal
        isOpen={state.isUploadModalOpen}
        onClose={() => actions.setIsUploadModalOpen(false)}
        onUploadSuccess={actions.refresh}
      />

      {/* Swipe Detail Modal */}
      <SwipeDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetail}
        record={detailRecord}
      />
    </div>
  );
}
